/**
 * ETF fundamentals card: AUM + Market Cap + 50-word summary + sector donut.
 * Only rendered when /api/etf-info returns is_etf=true.
 */
import { Loader2 } from "lucide-react";
import EtfTypeChip from "./EtfTypeChip";

const SECTOR_LABELS = {
  technology: "Technology",
  communication_services: "Communication Services",
  consumer_cyclical: "Consumer Cyclical",
  consumer_defensive: "Consumer Defensive",
  financial_services: "Financial Services",
  healthcare: "Healthcare",
  industrials: "Industrials",
  energy: "Energy",
  utilities: "Utilities",
  realestate: "Real Estate",
  basic_materials: "Basic Materials",
};

// Brand-aligned sector palette — varied around Paars / Groen / Nikkel goud / Champagne
const SECTOR_COLORS = [
  "#d7b5c9", // Paars
  "#88a17e", // Groen
  "#bfaf9d", // Nikkel goud
  "#9e8193", // Donker paars
  "#dbd6d2", // Champagne
  "#a3b594", // Lichtgroen
  "#c79ab5", // Donker paars warm
  "#7d8e6e", // Donkergroen
  "#a89687", // Donker goud
  "#e8ddd6", // Licht champagne
  "#8d6f80", // Diep paars
];

const fmtCurrency = (n, currency = "USD") => {
  if (!n || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T ${currency}`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B ${currency}`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M ${currency}`;
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
};

function SectorDonut({ sectors }) {
  const entries = Object.entries(sectors)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((acc, [, v]) => acc + v, 0);
  if (!entries.length || total === 0) {
    return <div className="text-xs text-zinc-500 font-mono">No sector data</div>;
  }

  // Donut geometry
  const R = 56;
  const STROKE = 22;
  const CIRC = 2 * Math.PI * R;
  let offset = 0;
  const slices = entries.map(([key, pct], i) => {
    const portion = pct / total;
    const length = portion * CIRC;
    const slice = {
      key,
      label: SECTOR_LABELS[key] || key,
      pct: portion,
      color: SECTOR_COLORS[i % SECTOR_COLORS.length],
      dasharray: `${length} ${CIRC - length}`,
      dashoffset: -offset,
    };
    offset += length;
    return slice;
  });

  return (
    <div className="flex items-center gap-5">
      <svg
        viewBox="-80 -80 160 160"
        width={144}
        height={144}
        className="shrink-0"
        data-testid="etf-sector-donut"
        aria-label="ETF sector allocation"
      >
        <circle cx={0} cy={0} r={R} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={STROKE} />
        {slices.map((s) => (
          <circle
            key={s.key}
            cx={0}
            cy={0}
            r={R}
            fill="none"
            stroke={s.color}
            strokeWidth={STROKE}
            strokeDasharray={s.dasharray}
            strokeDashoffset={s.dashoffset}
            transform="rotate(-90)"
          />
        ))}
      </svg>
      <ul className="text-[11px] font-mono space-y-1 flex-1 min-w-0">
        {slices.slice(0, 6).map((s) => (
          <li
            key={s.key}
            className="flex items-center justify-between gap-3 normal-case tracking-normal"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span
                className="inline-block w-2.5 h-2.5"
                style={{ background: s.color }}
              />
              <span className="text-zinc-300 truncate">{s.label}</span>
            </span>
            <span className="text-zinc-100 tabular-nums">{(s.pct * 100).toFixed(1)}%</span>
          </li>
        ))}
        {slices.length > 6 && (
          <li className="text-zinc-500 normal-case tracking-normal">
            + {slices.length - 6} more
          </li>
        )}
      </ul>
    </div>
  );
}

export default function EtfFundamentalsCard({ etf, loading, ytdTotalReturnPct, onTypeClick }) {
  if (loading) {
    return (
      <div
        data-testid="etf-fundamentals-card"
        className="border border-zinc-800 bg-[#121214] p-6 sm:p-8 flex items-center gap-3 text-xs font-mono text-zinc-500"
      >
        <Loader2 size={12} className="animate-spin" /> Loading ETF data…
      </div>
    );
  }
  if (!etf || !etf.is_etf) return null;

  const aumLabel = fmtCurrency(etf.aum, etf.currency || "USD");
  const expense = etf.expense_ratio != null ? `${Number(etf.expense_ratio).toFixed(2)}%` : "—";
  const ytdLabel =
    ytdTotalReturnPct != null && !isNaN(ytdTotalReturnPct)
      ? `${ytdTotalReturnPct >= 0 ? "+" : ""}${ytdTotalReturnPct.toFixed(2)}%`
      : "—";
  const ytdColor =
    ytdTotalReturnPct == null || isNaN(ytdTotalReturnPct)
      ? ""
      : ytdTotalReturnPct >= 0
      ? "text-[#00E676]"
      : "text-[#FF3B30]";

  return (
    <div
      data-testid="etf-fundamentals-card"
      className="border border-zinc-800 bg-[#121214] p-6 sm:p-7 flex flex-col gap-5"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-label">ETF · {etf.family || etf.category || "Fund"}</div>
        {etf.type && (
          <EtfTypeChip
            type={etf.type}
            size="md"
            showInfo
            onClick={onTypeClick ? () => onTypeClick(etf.type) : undefined}
          />
        )}
      </div>

      {etf.tagline && (
        <div
          data-testid="etf-tagline"
          className="text-base leading-snug text-zinc-100 italic normal-case tracking-normal border-l-2 border-[#d7b5c9] pl-3"
        >
          {etf.tagline}
        </div>
      )}

      {/* Three centered stats: Fund AUM · Expense Ratio · YTD TR */}
      <div
        data-testid="etf-key-stats"
        className="grid grid-cols-3 divide-x divide-zinc-900 border-y border-zinc-900 py-5"
      >
        <div className="px-2 flex flex-col items-center text-center">
          <div className="text-label mb-1.5">Fund AUM</div>
          <div data-testid="etf-aum" className="font-mono text-xl sm:text-2xl tabular-nums">
            {aumLabel}
          </div>
        </div>
        <div className="px-2 flex flex-col items-center text-center">
          <div className="text-label mb-1.5">Expense Ratio</div>
          <div
            data-testid="etf-expense-ratio"
            className="font-mono text-xl sm:text-2xl tabular-nums"
          >
            {expense}
          </div>
        </div>
        <div className="px-2 flex flex-col items-center text-center">
          <div className="text-label mb-1.5">Total Return YTD</div>
          <div
            data-testid="etf-ytd-return"
            className={`font-mono text-xl sm:text-2xl tabular-nums ${ytdColor}`}
          >
            {ytdLabel}
          </div>
        </div>
      </div>

      {etf.summary && (
        <p
          data-testid="etf-summary"
          className="text-sm leading-relaxed text-zinc-300 normal-case tracking-normal"
        >
          {etf.summary}
        </p>
      )}

      <div className="pt-1 border-t border-zinc-900">
        <div className="text-label mb-3 mt-3">Sector allocation</div>
        <SectorDonut sectors={etf.sector_weightings || {}} />
      </div>
    </div>
  );
}
